import { useRef, useState, useEffect, useMemo } from "react";
import { Box, Typography, Container} from "@mui/material";
import { motion } from "framer-motion";

/* ───────────────── Intersection Observer Hook ───────────────── */
const useOnScreen = (ref, rootMargin = "0px") => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      { rootMargin }
    );
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [ref, rootMargin, hasAnimated]);

  return isVisible;
};

/* ───────────────── Icon Components (Official Brand SVGs) ───────────────── */
const GmailIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 193">
    <path fill="#4285F4" d="M58.182 192.05V93.14L27.507 65.077L0 49.504v125.091c0 9.658 7.825 17.455 17.455 17.455z" />
    <path fill="#34A853" d="M197.818 192.05h40.727c9.659 0 17.455-7.826 17.455-17.455V49.505l-31.156 17.837l-27.026 25.798z" />
    <path fill="#EA4335" d="M58.182 93.14l-4.174-38.647l4.174-36.989L128 69.868l69.818-52.364l4.669 34.992l-4.669 40.644L128 145.504z" />
    <path fill="#FBBC04" d="M197.818 17.504V93.14L256 49.504V26.231c0-21.585-24.64-33.89-41.89-20.945z" />
    <path fill="#C5221F" d="M0 49.504l26.759 20.07L58.182 93.14V17.504L41.89 5.286C24.61-7.66 0 4.646 0 26.23z" />
  </svg>
);

const DropboxIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 218">
    <path fill="#0061FF" d="M63.995 0L0 40.771l63.995 40.772L128 40.771z" />
    <path fill="#0061FF" d="M128 40.771l63.995-40.77L256 40.77l-64.005 40.772z" />
    <path fill="#0061FF" d="M0 122.321l63.995 40.772L128 122.32l-64.005-40.77z" />
    <path fill="#0061FF" d="M128 122.321l63.995 40.772L256 122.32l-64.005-40.77z" />
    <path fill="#0061FF" opacity="0.7" d="M64.01 176.771L128.005 217.556L192.005 176.771L128.005 135.999z" />
  </svg>
);

const LinkedInIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 256">
    <rect width="256" height="256" rx="28" fill="#0A66C2" />
    <path fill="#fff" d="M73.616 107.5v84.874H44.098V107.5zM58.857 95.072c10.316 0 16.737-6.84 16.737-15.383c-.192-8.735-6.42-15.384-16.544-15.384c-10.126 0-16.74 6.649-16.74 15.384c0 8.544 6.421 15.383 16.356 15.383z" />
    <path fill="#fff" d="M101.61 192.374h29.518v-47.38c0-2.535.184-5.066.93-6.878c2.035-5.063 6.668-10.307 14.45-10.307c10.19 0 14.269 7.772 14.269 19.166v45.399h29.519v-48.657c0-26.065-13.903-38.19-32.44-38.19c-15.204 0-21.876 8.5-25.589 14.283h.198V107.5H101.61c.388 8.352 0 84.874 0 84.874z" />
  </svg>
);

const RedditIcon = ({ size = 32 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 256 256">
    <circle cx="128" cy="128" r="128" fill="#FF4500" />
    <path fill="#fff" d="M213.15 128.946c0-10.63-8.63-19.26-19.26-19.26c-5.21 0-9.91 2.09-13.36 5.47c-13.18-9.51-31.37-15.67-51.57-16.39l8.78-41.39l28.68 6.09c.31 7.3 6.3 13.14 13.69 13.14c7.58 0 13.72-6.14 13.72-13.72s-6.14-13.72-13.72-13.72c-5.4 0-10.05 3.13-12.28 7.67l-32.11-6.82c-1.79-.38-3.56.71-3.97 2.49l-9.8 46.22c-20.58.47-39.13 6.61-52.53 16.23c-3.42-3.29-8.06-5.32-13.18-5.32c-10.63 0-19.26 8.63-19.26 19.26c0 7.42 4.22 13.86 10.39 17.09c-.26 1.79-.39 3.62-.39 5.47c0 27.84 32.41 50.39 72.39 50.39s72.39-22.55 72.39-50.39c0-1.83-.13-3.64-.38-5.42c6.24-3.21 10.5-9.68 10.5-17.14" />
    <circle cx="96" cy="143" r="15" fill="#FF4500" />
    <circle cx="160" cy="143" r="15" fill="#FF4500" />
    <path fill="#FF4500" d="M100.57 179.91c1.66 1.66 4.36 1.66 6.02 0c6.67-6.67 17.45-9.9 21.41-9.9s14.74 3.23 21.41 9.9c1.66 1.66 4.36 1.66 6.02 0c1.66-1.66 1.66-4.36 0-6.02c-9.07-9.07-22.36-12.14-27.43-12.14s-18.36 3.07-27.43 12.14c-1.66 1.67-1.66 4.37 0 6.02" />
  </svg>
);

const iconComponents = {
  Gmail: GmailIcon,
  Dropbox: DropboxIcon,
  LinkedIn: LinkedInIcon,
  Reddit: RedditIcon,
};

/* ───────────────── Integration Data ───────────────── */
const integrations = [
  { name: "Gmail", color: "#EA4335" },
  { name: "Dropbox", color: "#0061FF" },
  { name: "LinkedIn", color: "#0A66C2" },
  { name: "Reddit", color: "#FF4500" },
];

/* ───────────────── Arc Math ───────────────── */
// ViewBox dimensions — the SINGLE source of truth for all coordinates
const viewW = 1200;
const viewH = 480;

// Arc control points: starts bottom-left, peaks top-center, ends bottom-right
// Keep some padding so icons at ends aren't clipped
const P0 = { x: 60,        y: viewH - 40 };
const P1 = { x: viewW / 2, y: 40 };
const P2 = { x: viewW - 60, y: viewH - 40 };

const arcPath = `M ${P0.x} ${P0.y} Q ${P1.x} ${P1.y} ${P2.x} ${P2.y}`;

// Quadratic Bezier point at parameter t
const getPointOnArc = (t) => {
  const mt = 1 - t;
  return {
    x: mt * mt * P0.x + 2 * mt * t * P1.x + t * t * P2.x,
    y: mt * mt * P0.y + 2 * mt * t * P1.y + t * t * P2.y,
  };
};

// t values — evenly spread, avoiding extreme ends
const tValues = [0.1, 0.36, 0.64, 0.9];

// Icon box size IN SVG viewBox units (not px)
// The SVG is ~1200 viewBox units wide; rendered ~1100px → 1 unit ≈ 0.92px.
// We want ~64px icons, so ~70 viewBox units.
const ICON_BOX = 80; // viewBox units
const ICON_SVG = 38; // viewBox units (icon inside the box)
const LABEL_OFFSET = 54; // below icon center

/* ───────────────── Main Component ───────────────── */
const AboutSec5 = () => {
  // const theme = useTheme();
  // const isXs = useMediaQuery(theme.breakpoints.down("sm")); 

  const sectionRef = useRef(null);
  const badgeRef   = useRef(null);
  const headingRef = useRef(null);
  const arcRef     = useRef(null);
  const descRef    = useRef(null);

  // const sectionVisible = useOnScreen(sectionRef);
  const badgeVisible   = useOnScreen(badgeRef);
  const headingVisible = useOnScreen(headingRef);
  const arcVisible     = useOnScreen(arcRef);
  const descVisible    = useOnScreen(descRef);

  const fadeInUp = {
    hidden:  { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
  };

  // Pre-compute arc positions once
  const arcPoints = useMemo(() => tValues.map((t) => getPointOnArc(t)), []);

  return (
    <Box
      ref={sectionRef}
      sx={{
        width: "100%",
        background: "#FFFFFF",
        py: { xs: 6, sm: 8, md: 10 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Ambient glow top-right */}
      <motion.div
        animate={{ opacity: [0.03, 0.07, 0.03], scale: [1, 1.1, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          width: "500px", height: "500px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(64,122,255,0.08) 0%, transparent 70%)",
          top: "-15%", right: "-10%",
          pointerEvents: "none",
        }}
      />

      {/* Ambient glow bottom-left */}
      <motion.div
        animate={{ opacity: [0.02, 0.06, 0.02], scale: [1, 1.15, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        style={{
          position: "absolute",
          width: "400px", height: "400px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(26,115,232,0.06) 0%, transparent 70%)",
          bottom: "-10%", left: "-8%",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" mb="0">
        {/* ─── Badge ─── */}
        <motion.div ref={badgeRef} initial="hidden" animate={badgeVisible ? "visible" : "hidden"} variants={fadeInUp}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <motion.div
              animate={badgeVisible ? { boxShadow: ["0 0 0px rgba(64,122,255,0)", "0 0 20px rgba(64,122,255,0.15)", "0 0 0px rgba(64,122,255,0)"] } : {}}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              style={{ borderRadius: "20px" }}
            >
              <Typography sx={{
                backgroundColor: "#f0f7ff", color: "#1a73e8",
                px: 3, py: 1, borderRadius: "20px",
                fontSize: { xs: "11px", sm: "12px", md: "14px" },
                fontWeight: 600, fontFamily: "Poppins",
                textTransform: "uppercase", letterSpacing: "1.5px",
                border: "1px solid #e0ecff",
              }}>
                Integration
              </Typography>
            </motion.div>
          </Box>
        </motion.div>

        {/* ─── Heading Line 1 ─── */}
        <motion.div ref={headingRef} initial="hidden" animate={headingVisible ? "visible" : "hidden"} variants={fadeInUp}>
          <Typography variant="h2" sx={{
            textAlign: "center", color: "#1a73e8", fontFamily: "Poppins",
            fontWeight: 600, fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.4rem" },
            mb: 0, lineHeight: 1.25,
          }}>
            Integrate Everything.
          </Typography>
        </motion.div>

        {/* ─── Heading Line 2 ─── */}
        <motion.div
          initial="hidden"
          animate={headingVisible ? "visible" : "hidden"}
          variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.15, ease: "easeOut" } } }}
        >
          <Typography variant="h2" sx={{
            textAlign: "center", color: "#1a73e8", fontFamily: "Poppins",
            fontWeight: 600, fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.4rem" },
            mb: {sx: 0, md: -20}, lineHeight: 1.25,
          }}>
            Automate Anything.
          </Typography>
        </motion.div>
      </Container>

      {/* ═══════════════ ARC SECTION ═══════════════ */}
      <Box
        ref={arcRef}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1100px",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 0 },
          my: 0,
          mt: {sx: 0 , md: -15}
        }}
      >
        <svg
          viewBox={`0 0 ${viewW} ${viewH}`}
          preserveAspectRatio="xMidYMid meet"
          width="100%"
          style={{ display: "block", overflow: "visible" }}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* ── Soft glow arc (background layer) ── */}
          <motion.path
            d={arcPath}
            stroke="#dce9fa"
            strokeWidth="14"
            strokeLinecap="round"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={arcVisible ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeInOut", delay: 0.2 }}
          />

          {/* ── Main arc line ── */}
          <motion.path
            d={arcPath}
            stroke="#c2d4ee"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={arcVisible ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeInOut", delay: 0.35 }}
          />

          {/* ── Subtle dot markers along the arc ── */}
          {[0.05, 0.2, 0.3, 0.5, 0.7, 0.8, 0.95].map((t, i) => {
            const pt = getPointOnArc(t);
            return (
              <motion.circle
                key={`arc-dot-${i}`}
                cx={pt.x} cy={pt.y} r="4"
                fill="#c8d8ec"
                initial={{ opacity: 0, scale: 0 }}
                animate={arcVisible ? { opacity: 0.6, scale: 1 } : { opacity: 0, scale: 0 }}
                transition={{ duration: 0.4, delay: 1.2 + i * 0.1, ease: "easeOut" }}
              />
            );
          })}

          {/* ── Icons — positioned in SVG coordinate space ── */}
          {integrations.map((item, index) => {
            const pt = arcPoints[index];
            const IconComp = iconComponents[item.name];
            // Icon card top-left corner so the card is centered on the arc point
            const cardX = pt.x - ICON_BOX / 2;
            const cardY = pt.y - ICON_BOX / 2;

            return (
              <motion.g
                key={item.name}
                initial={{ opacity: 0, scale: 0 }}
                animate={arcVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                // SVG transform-origin must be set to the arc point for correct scale origin
                style={{ transformOrigin: `${pt.x}px ${pt.y}px` }}
                transition={{
                  duration: 0.6,
                  delay: 0.8 + index * 0.22,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
              >
                {/* Pulse ring */}
                <motion.rect
                  x={cardX} y={cardY}
                  width={ICON_BOX} height={ICON_BOX}
                  rx="16" ry="16"
                  fill="none"
                  stroke={item.color}
                  strokeWidth="1.5"
                  strokeOpacity="0.25"
                  animate={arcVisible ? { scale: [1, 1.55, 1], opacity: [0.25, 0, 0.25] } : {}}
                  style={{ transformOrigin: `${pt.x}px ${pt.y}px` }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 + index * 0.4 }}
                />

                {/* Card background — white rounded rect */}
                <rect
                  x={cardX} y={cardY}
                  width={ICON_BOX} height={ICON_BOX}
                  rx="16" ry="16"
                  fill="#FFFFFF"
                  stroke="#e4ecf6"
                  strokeWidth="1.5"
                  filter="url(#cardShadow)"
                />

                {/* Icon rendered via foreignObject — centered in the card */}
                <foreignObject
                  x={cardX + (ICON_BOX - ICON_SVG) / 2}
                  y={cardY + (ICON_BOX - ICON_SVG) / 2}
                  width={ICON_SVG}
                  height={ICON_SVG}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComp size={ICON_SVG} />
                  </div>
                </foreignObject>

                {/* Label below the card */}
                <motion.text
                  x={pt.x}
                  y={cardY + ICON_BOX + LABEL_OFFSET - ICON_BOX + 22}
                  textAnchor="middle"
                  fill="#90a4ae"
                  fontSize="10"
                  fontFamily="Poppins, sans-serif"
                  fontWeight="500"
                  letterSpacing="0.5"
                  initial={{ opacity: 0 }}
                  animate={arcVisible ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ delay: 1.4 + index * 0.2, duration: 0.5 }}
                >
                  {item.name}
                </motion.text>
              </motion.g>
            );
          })}

          {/* Drop shadow filter for icon cards */}
          <defs>
            <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="#000000" floodOpacity="0.055" />
              <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#000000" floodOpacity="0.04" />
            </filter>
          </defs>
        </svg>
      </Box>

      {/* ─── Description ─── */}
      <Container maxWidth="lg">
        <motion.div
          ref={descRef}
          initial="hidden"
          animate={descVisible ? "visible" : "hidden"}
          variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.15, ease: "easeOut" } } }}
        >
          <Typography variant="body1" sx={{
            textAlign: {xs: "left" , md: "center"}, color: "#546e7a",
            fontFamily: "Poppins",
            fontSize: { xs: "13px", sm: "14px", md: "16px" },
            maxWidth: "640px", mx: "auto",
            lineHeight: 1.8,
            mt: { xs: 3, sm: 4, md: 5 },
            px: { xs: 2, sm: 0 },
          }}>
            Connect your existing workflows with powerful AI capabilities.
            Whether it's Notion, Gmail, Reddit, or X (Twitter), our solutions
            integrate effortlessly — keeping your data unified and your
            operations efficient.
          </Typography>
        </motion.div>

        {/* Bottom decorative line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={descVisible ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
          style={{ transformOrigin: "center" }}
        >
          <Box sx={{
            width: "80px", height: "2px",
            background: "linear-gradient(90deg, transparent, #e0ecff, transparent)",
            mx: "auto", mt: { xs: 4, md: 5 },
          }} />
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutSec5;